// Budget Center.
import {
  DEMO_CLIENT, MARKETS, VERTICALS, dailyRows, iso, monthKey, monthlyBudget, rng, round2, today,
} from '../client';

export const TERRITORY_ID_BASE = 7000;
export const territoryId = (m) => TERRITORY_ID_BASE + m.id;

function parsePeriod(params) {
  const raw = params.get('period');
  const d = raw ? new Date(`${raw.slice(0, 7)}-01T00:00:00`) : today();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function segmentAmount(period, v, m) {
  return Math.round((monthlyBudget(period) * v.share * m.share) / 50) * 50;
}

export function budgetCenter(params) {
  const period = parsePeriod(params);
  const now = today();
  const curKey = monthKey(now);
  const key = monthKey(period);
  const isCurrent = key === curKey;
  const isPast = key < curKey;
  const rows = dailyRows().filter((r) => r.date.startsWith(key));
  const daysInMonth = new Date(period.getFullYear(), period.getMonth() + 1, 0).getDate();
  const daysElapsed = isCurrent ? Math.max(1, now.getDate() - 1) : daysInMonth;
  const r = rng(Number(key.replace('-', '')) + 7);

  let idSeq = Number(key.replace('-', '')) * 100;
  const segments = [];
  VERTICALS.forEach((v) => {
    MARKETS.forEach((m) => {
      idSeq += 1;
      const amount = segmentAmount(period, v, m);
      const spend = rows.filter((x) => x.vertical === v.name && x.market === m.name).reduce((s, x) => s + x.spend, 0);
      const hasAdj = isCurrent && v.name === 'Bath' && m.name === 'Charlotte';
      const adjustment = hasAdj ? 2500 : 0;
      const effective = amount + adjustment;
      const projected = isPast ? spend : (spend / daysElapsed) * daysInMonth;
      const remainingDays = Math.max(1, daysInMonth - daysElapsed);
      segments.push({
        id: idSeq,
        budget_id: idSeq,
        source_budget_id: idSeq,
        vertical: v.id,
        vertical_ids: [v.id],
        vertical_data: { id: v.id, vertical: v.name },
        location: territoryId(m),
        location_ids: [territoryId(m)],
        location_data: { id: territoryId(m), name: m.name },
        channel: 1,
        channel_data: { id: 1, name: 'Facebook' },
        amount: amount.toFixed(2),
        starting_budget: amount.toFixed(2),
        adjustment_total: adjustment.toFixed(2),
        effective_amount: effective.toFixed(2),
        month_to_date_spend: round2(spend).toFixed(2),
        projected_spend: round2(projected).toFixed(2),
        remaining_per_day: round2(Math.max(0, effective - spend) / remainingDays).toFixed(2),
        pacing_percentage: effective ? Math.round((projected / effective) * 1000) / 10 : null,
        starts_on: iso(period),
        notes: '',
        workflow_status: isPast ? 'completed' : isCurrent ? 'active' : 'draft',
        has_pending_turn_off: false,
        auto_applies_at: null,
        is_turned_off: false,
        adjustments: hasAdj
          ? [{ id: idSeq * 10 + 1, amount_delta: '2500.00', effective_date: iso(new Date(period.getFullYear(), period.getMonth(), 9)), notes: 'Mid-month boost — strong bath demand' }]
          : [],
        vertical_id: v.id,
        location_id: territoryId(m),
      });
      // keep r() advancing so totals vary per month
      r();
    });
  });

  const total = segments.reduce((s, x) => s + Number(x.amount), 0);
  const effectiveTotal = segments.reduce((s, x) => s + Number(x.effective_amount), 0);
  const prevPeriod = new Date(period.getFullYear(), period.getMonth() - 1, 1);
  const prevSegments = [];
  VERTICALS.forEach((v) => MARKETS.forEach((m) => prevSegments.push({
    vertical_id: v.id, location_id: territoryId(m), channel_id: 1, amount: segmentAmount(prevPeriod, v, m).toFixed(2),
  })));
  const prevTotal = prevSegments.reduce((s, x) => s + Number(x.amount), 0);
  const actor = { name: 'Dana Whitfield', email: DEMO_CLIENT.contact_email };
  const staff = { name: 'Lead Gurus Team', email: 'team@leadgurus.example' };
  const stamp = (day, hh) => `${iso(new Date(period.getFullYear(), period.getMonth() - (day < 0 ? 1 : 0), Math.abs(day)))}T${hh}:00:00Z`;

  const status = isPast ? 'completed' : isCurrent ? 'active' : 'draft';
  return {
    period: iso(period),
    status,
    client: { id: DEMO_CLIENT.id, name: DEMO_CLIENT.name },
    current_budget: { amount: total.toFixed(2), effective_amount: effectiveTotal.toFixed(2), segment_count: segments.length },
    previous_budget: { amount: prevTotal.toFixed(2), effective_amount: prevTotal.toFixed(2), segment_count: prevSegments.length },
    previous_segments: prevSegments,
    segments,
    live_budget_ids: status === 'draft' ? [] : segments.map((s) => s.budget_id),
    draft_budget_ids: status === 'draft' ? segments.map((s) => s.budget_id) : [],
    has_draft: status === 'draft',
    has_unsubmitted_draft: false,
    seeded_from_previous: status === 'draft',
    seed_source_period: iso(prevPeriod),
    activity_summary: status === 'draft' ? {} : {
      saved_draft: { actor, created_at: stamp(-24, '14') },
      submitted: { actor, created_at: stamp(-25, '15') },
      approved: { actor: staff, created_at: stamp(-26, '13') },
      marked_live: { actor: staff, created_at: stamp(1, '09') },
    },
    history: status === 'draft' ? [] : [
      {
        id: 1, summary: 'Bath · Charlotte adjusted +$2,500', actor, created_at: stamp(9, '16'),
        entity_label: 'Bath · Charlotte', changed_fields: ['adjustment_total'], before: { adjustment_total: '0.00' }, after: { adjustment_total: '2500.00' },
      },
      {
        id: 2, summary: 'Budget marked live', actor: staff, created_at: stamp(1, '09'),
        entity_label: 'All segments', changed_fields: ['workflow_status'], before: { workflow_status: 'approved' }, after: { workflow_status: 'active' },
      },
    ],
    turn_off_batch: null,
  };
}

function scopeSpend(params) {
  const key = monthKey(parsePeriod(params));
  const rows = dailyRows().filter((r) => r.date.startsWith(key));
  const out = [];
  VERTICALS.forEach((v) => MARKETS.forEach((m) => {
    const spend = rows.filter((x) => x.vertical === v.name && x.market === m.name).reduce((s, x) => s + x.spend, 0);
    out.push({ vertical_id: v.id, location_id: territoryId(m), month_to_date_spend: round2(spend).toFixed(2) });
  }));
  return out;
}

function comments(params) {
  const p = parsePeriod(params);
  if (monthKey(p) > monthKey(today())) return [];
  return [
    { id: 1, author: { name: 'Dana Whitfield', email: DEMO_CLIENT.contact_email }, body: 'Bath is booking well in Charlotte — can we lean in a bit more there this month?', created_at: `${iso(new Date(p.getFullYear(), p.getMonth(), 8))}T15:20:00Z` },
    { id: 2, author: { name: 'Lead Gurus Team', email: 'team@leadgurus.example' }, body: 'Done — added $2,500 to Bath · Charlotte starting the 9th. We\'ll watch cost per appointment and report back.', created_at: `${iso(new Date(p.getFullYear(), p.getMonth(), 9))}T13:05:00Z` },
  ];
}

export const routes = [
  ['GET', 'dashboard/client-budgets/budget-center/scope-spend', ({ params }) => scopeSpend(params)],
  ['GET', 'dashboard/client-budgets/budget-center/comments', ({ params }) => comments(params)],
  ['GET', 'dashboard/client-budgets/budget-center', ({ params }) => budgetCenter(params)],
  ['POST', /^dashboard\/client-budgets\/budget-center\/(save-draft|approve|discard)\/?$/, ({ params, body }) => {
    const p = new URLSearchParams(params);
    if (body?.period) p.set('period', body.period);
    return budgetCenter(p);
  }],
  ['GET', 'dashboard/client-budgets/budget-verticals', () => VERTICALS.map((v) => ({ id: v.id, vertical: v.name }))],
  ['GET', 'dashboard/pacing-holds', () => []],
];
